import React, { createContext, useContext, useReducer, type ReactNode } from 'react';

export type PKGOPS_State = 
  | 'SETUP' 
  | 'CHECKLIST_FILL' 
  | 'PENDING_PRODUCTION' 
  | 'PENDING_REVERIFICATION' 
  | 'COMPLETED';

export type PKGOPS_Type = 
    | "Temperatures & Humidity"
    | "Code Verification"
    | "PAPA"
    | "PQI"
    | "Seal Integrity"
    | "Cream Percentage"
    | "Quality Wall Records"
    | null;

interface SessionData {
    tourId: string | null;
    date: string;
    time: string;
    site: string;
    lineNo: string;
    qaExecutive: string;
    productionIncharge: string;
    shift: string;
    pkgopsType: PKGOPS_Type;
    status: string;
}

interface PackagingOperationsState {
    currentState: PKGOPS_State;
    sessionData: SessionData;
    checklistData: any; // To store data from sub-checklists
    deviations: any[]; // To store deviated rows for PENDING_PRODUCTION state
}

type Action =
    | { type: 'SET_STATE'; payload: PKGOPS_State }
    | { type: 'SET_SESSION_DATA'; payload: Partial<SessionData> }
    | { type: 'SET_CHECKLIST_DATA'; payload: any }
    | { type: 'SET_DEVIATIONS'; payload: any[] }
    | { type: 'START_TOUR'; payload: SessionData };

const initialState: PackagingOperationsState = {
    currentState: 'SETUP',
    sessionData: {
        tourId: null,
        date: '',
        time: '',
        site: 'Rajpura',
        lineNo: 'Line 1',
        qaExecutive: '',
        productionIncharge: '',
        shift: 'Shift 1',
        pkgopsType: null,
        status: 'Pending QA'
    },
    checklistData: null,
    deviations: []
};

const PackagingOperationsContext = createContext<{
    state: PackagingOperationsState;
    dispatch: React.Dispatch<Action>;
} | undefined>(undefined);

function packagingOperationsReducer(state: PackagingOperationsState, action: Action): PackagingOperationsState {
    switch (action.type) {
        case 'SET_STATE':
            return { ...state, currentState: action.payload };
        case 'SET_SESSION_DATA':
            return { ...state, sessionData: { ...state.sessionData, ...action.payload } };
        case 'SET_CHECKLIST_DATA':
            return { ...state, checklistData: action.payload };
        case 'SET_DEVIATIONS':
            return { ...state, deviations: action.payload };
        case 'START_TOUR':
            return { 
                ...state, 
                currentState: 'CHECKLIST_FILL', 
                sessionData: { ...action.payload, status: 'In Progress (QA)' } 
            };
        default:
            return state;
    }
}

export const PackagingOperationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(packagingOperationsReducer, initialState);

    return (
        <PackagingOperationsContext.Provider value={{ state, dispatch }}>
            {children}
        </PackagingOperationsContext.Provider>
    );
};

export const usePackagingOperationsContext = () => {
    const context = useContext(PackagingOperationsContext);
    if (!context) {
        throw new Error('usePackagingOperationsContext must be used within a PackagingOperationsProvider');
    }
    return context;
};
